import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Popup from 'reactjs-popup';
import moment from 'moment';
import { PhotoProvider, PhotoView } from 'react-photo-view';

import Badge from 'common/badge/badge';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import './checklist-item.scss';
import EmployeeModal from 'components/checklist/employee-modal/employee-modal';

// Assets
import { ReactComponent as ActionIcon } from 'assets/icons/action.svg';
import { ReactComponent as TaskTickIcon } from 'assets/icons/icon_task_tick.svg';
import { ReactComponent as EllipseIcon } from 'assets/icons/ellipse.svg';
import { ReactComponent as AttachmentIcon } from 'assets/icons/icon_attach.svg';
import { ReactComponent as MessageIcon } from 'assets/icons/icon_message.svg';
import { ReactComponent as CheckedPersonIcon } from 'assets/icons/checked-person.svg';
import { ReactComponent as TaskTickAbnormalIcon } from 'assets/icons/task_tick_icon.svg';
import { AxiosResponse } from 'axios';
import useApi from 'hooks/useApi';
import useLoadingStore from 'zustands/useLoadingStore';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import useErrorLog from 'hooks/useErrorLog';
import { ICheckedItem, IChecklist } from 'types/checklist';
import useStoreStore from 'zustands/useStoreStore';

const ChecklistItem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { CHECK_SEQ, CHECK_DATE } = location.state.item;

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const { storeSeqNumber } = useStoreStore();
  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [checkItem, setCheckItem] = React.useState<IChecklist | null>(null);
  const [selectedCheckItem, setSelectedCheckItem] = React.useState<ICheckedItem>();
  const [selectedChecklist, setSelectedChecklist] = React.useState<{ title: string; value: string }[]>();
  const [checkedItems, setCheckedItems] = React.useState<ICheckedItem[]>();
  const [employeeModal, setEmployeeModal] = React.useState<boolean>(false);
  const [employeesList, setEmployeesList] = React.useState<Array<any>>([]);
  const [selectedEmployees, setSelectedEmployees] = React.useState<string>();

  const badgesList = ['나매니저', '홍직원', '홍직원', '홍직원', '홍직원', '홍직원', '홍직원', '홍직원', '홍직원'];

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getAllCheckSchedules`, {
        CHECK_SEQ: CHECK_SEQ,
        DATE: CHECK_DATE,
      });

      if (data.resultCode === 0) {
        setCheckItem(data.resultData.checkdata[0]);
        if (data.resultData.resultdata.length > 0) {
          setCheckedItems(data.resultData.resultdata);
          setSelectedCheckItem(data.resultData.resultdata[0]);
          formatChecklist(data.resultData.resultdata[0].CHECK_LIST);
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getAllCheckSchedules');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // {체크리스트: 결과} 데이터 배열 가공
  const formatChecklist = (arr: string) => {
    const checklistObj: string[] = [];
    const checkResultObj: string[] = [];
    arr.split('@@').map((i, _i) => {
      if (_i % 2 === 0) {
        checklistObj.push(i);
      } else {
        checkResultObj.push(i);
      }
    });
    const checklist = [];
    for (let i = 0; i < checklistObj.length; i++) {
      checklist.push({ title: checklistObj[i], value: checkResultObj[i] });
    }
    setSelectedChecklist(checklist);
  };

  // 체크 직원 데이터 변경
  const changeCheckEmp = (item: ICheckedItem) => {
    if (selectedCheckItem?.EMP_NAME !== item.EMP_NAME) {
      setSelectedCheckItem(item);
      formatChecklist(item.CHECK_LIST);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const toggleEmployeeModal = (id: string) => {
    setSelectedEmployees(id);
    setEmployeeModal(!employeeModal);
  };

  const attachments = [
    'https://cdn.pixabay.com/photo/2016/12/26/17/28/spaghetti-1932466__340.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
    'https://www.teenaagnel.com/wp-content/uploads/2019/12/food-photography-in-dubai.jpg',
  ];

  const handleAccept = () => {
    navigate(`/add-checklist-form`, {
      state: {
        checkItemData: checkItem,
      },
    });
  };

  // 체크리스트 삭제 요청
  const deleteChecklistItem = async () => {
    if (!checkItem) return;

    try {
      setLoadingState({ isFetchLoading: true });
      const list = checkItem.LIST.split('@@');
      const emps = checkItem.EMP_SEQ?.split('@').map((i) => ({ EMP_SEQ: i }));
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/checkUpdate`, {
        STORE_SEQ: storeSeqNumber,
        CHECK_SEQ: checkItem.CHECK_SEQ,
        TITLE: checkItem.TITLE,
        LIST: list,
        CHECK_EMP_FLAG: checkItem.EMP_SEQ ? '1' : '0',
        EMP_SEQS: emps ?? null,
        END_TIME: checkItem.END_TIME,
        PHOTO_CHECK: checkItem.PHOTO_CHECK,
        CLOSE_FLAG: '1',
      });

      if (data.resultCode === 0) {
        navigate('/checklist');
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/checkUpdate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const handleReject = () => {
    setModalState({
      isModalShow: true,
      title: '체크리스트 삭제',
      desc: '해당 체크리스트를 삭제합니다',
      okBtnText: '삭제',
      okCallback: () => deleteChecklistItem(),
      isWarning: true,
    });
  };

  let checkStatus = '미체크';
  if (selectedCheckItem?.CHECK_LIST) {
    if (selectedCheckItem?.CHECK_LIST.split('@@').includes('2')) {
      checkStatus = '체크이상';
    } else {
      checkStatus = '체크정상';
    }
  }

  return (
    <>
      <div className="checklist-item__background">
        <PlainNavbar backgroundColor="#f5f5f5" route="checklist" customButton={<></>}>
          <div className="checklist-item">
            <div className="checklist-item__head">
              <div className="checklist-item__head__head">
                <div className="checklist-item__head__head-text">{checkItem?.TITLE}</div>
                <div className="checklist-item__head__head-badge">
                  <Badge text={checkStatus} type={checkStatus === '체크이상' ? 'type2' : ''} />
                </div>
                <div className="checklist-item__head__head-options">
                  <Popup
                    trigger={
                      <div className="checklist-item__popup__icon">
                        <ActionIcon />
                      </div>
                    }
                    position="bottom center"
                    className="checklist-item__popup">
                    <div className="checklist-item__popup">
                      <div className="checklist-item__popup__accept" onClick={handleAccept}>
                        수정
                      </div>
                      <div className="checklist-item__popup__reject" onClick={handleReject}>
                        삭제
                      </div>
                    </div>
                  </Popup>
                </div>
              </div>

              <div className="checklist-item__head__subhead-text">
                {checkedItems ? (
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <CheckedPersonIcon />
                    {checkedItems.map((i) => {
                      return (
                        <button
                          onClick={() => changeCheckEmp(i)}
                          style={{
                            marginLeft: 10,
                            padding: '6px 12px',
                            borderRadius: 50,
                            backgroundColor: selectedCheckItem?.CS_SEQ === i.CS_SEQ ? '#6f6f6f' : 'white',
                            color: selectedCheckItem?.CS_SEQ === i.CS_SEQ ? 'white' : 'black',
                          }}>
                          {i.EMP_NAME}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  '아직 미체크된 체크리스트입니다'
                )}
              </div>
            </div>
            <div className="checklist-item-cards">
              <div className="checklist-item__card-profile-changes">
                <div className="checklist-item-basic-info__head">기본정보</div>

                <table className="checklist-item__body__table">
                  <tr>
                    <td className="checklist-item__body__left">
                      <div className="checklist-item-basic-info__body">
                        <div className="checklist-item-basic-info"></div>
                        <div className="checklist-item-basic-info__row">
                          <div className="checklist-item-basic-info__col1">
                            <div className="checklist-item-basic-info__col1__line">체크예정 시간</div>
                            <div className="checklist-item-basic-info__col1__line">확인직원</div>
                            <div className="checklist-item-basic-info__col1__line">확인시간</div>
                            {checkItem?.EMP_NAME2 === null && (
                              <div className="checklist-item-basic-info__col1__line">담당직원</div>
                            )}
                          </div>
                          <div className="checklist-item-basic-info__col2">
                            <div className="checklist-item-basic-info__col1__line">
                              {checkItem?.END_TIME ? checkItem?.END_TIME : '미사용'}
                            </div>
                            <div className="checklist-item-basic-info__col1__line">
                              {selectedCheckItem?.EMP_NAME ? selectedCheckItem?.EMP_NAME : '체크전'}
                            </div>
                            <div className="checklist-item-basic-info__col1__line">
                              {selectedCheckItem?.CHECK_TIME
                                ? moment(selectedCheckItem?.CHECK_TIME).format('HH:mm')
                                : '체크전'}
                            </div>
                            {checkItem?.EMP_NAME2 === null && (
                              <div className="checklist-item-basic-info__col1__line">미사용</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    {!!checkItem?.EMP_NAME2 && (
                      <td className="checklist-item__body__right">
                        <div className="checklist-item-basic-info__body">
                          <div className="checklist-item-basic-info">
                            ‍🧑‍💻 담당직원{' '}
                            <span className="checklist-item-basic-info__number">
                              {checkItem?.EMP_NAME2.split('@').filter((emp) => !!emp && emp.length > 0).length}
                            </span>
                          </div>
                          <div className="checklist-item-basic-info__badge-grid">
                            {checkItem?.EMP_NAME2.split('@')
                              .filter((emp) => !!emp && emp.length > 0)
                              .map((badge: string, _i: number) => (
                                <span className="checklist-item-basic-info__badge" key={`${_i}_${badge}`}>
                                  {badge}
                                </span>
                              ))}
                          </div>
                        </div>
                      </td>
                    )}
                  </tr>
                </table>
              </div>
            </div>
            <div className="checklist-item-cards">
              <div className="checklist-item__card-profile-changes">
                <div className="checklist-item-setting__head">설정</div>
                <div className="checklist-item-setting__body">
                  <table className="checklist-item-setting__table">
                    <tr>
                      <td className="checklist-item-setting__table__left">작업 결과를 담당직원만 체크할 수 있나요?</td>
                      <td className="checklist-item-setting__table__right">
                        {checkItem?.EMP_NAME2 ? '네, 담당직원만' : '아니요, 전직원'}
                      </td>
                    </tr>
                    <tr>
                      <td className="checklist-item-setting__table__left">사진을 등록해야만 체크완료가 가능한가요?</td>
                      <td className="checklist-item-setting__table__right">
                        {checkItem?.PHOTO_CHECK === '1' ? '네, 사진 필요' : '아니요, 항목만 체크'}
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
            <div className="checklist-item-cards">
              <div className="checklist-item__card-profile-changes">
                <div className="checklist__head">체크리스트</div>
                <div className="checklist__body">
                  <div className="checklist__list">
                    {selectedChecklist && selectedChecklist?.length > 0
                      ? selectedChecklist?.map((item, _i) => {
                          return (
                            <div className="checklist__item">
                              <div className="checklist__item__item">
                                <EllipseIcon />
                                <span className="checklist__item__item__text">{item.title}</span>
                              </div>
                              <div className="checklist__item__type">
                                {item.value === '1' ? (
                                  <TaskTickIcon width={20} height={20} />
                                ) : (
                                  <TaskTickAbnormalIcon width={20} height={20} />
                                )}
                                <span
                                  className={
                                    item.value === '1'
                                      ? 'checklist__item__type__text'
                                      : 'checklist__item__type__text-abnormal'
                                  }>
                                  {item.value === '1' ? '정상' : '이상'}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      : checkItem &&
                        checkItem.LIST.split('@@').map((i) => {
                          return (
                            <div className="checklist__item">
                              <div className="checklist__item__item">
                                <EllipseIcon />
                                <span className="checklist__item__item__text">{i}</span>
                              </div>
                            </div>
                          );
                        })}
                  </div>
                  {selectedCheckItem?.IMAGE_LIST && (
                    <div className="checklist__attachments">
                      <div className="checklist__attachments__head">
                        <AttachmentIcon />
                        <span className="checklist__attachments__head-text">사진 첨부</span>
                      </div>
                      <div className="checklist__attachments__body image-content">
                        <PhotoProvider>
                          {selectedCheckItem?.IMAGE_LIST.split('@').map((attachment: string, _i: number) => {
                            return (
                              <div className='className="checklist__attachments__images' key={attachment}>
                                <PhotoView src={attachment} key={attachment}>
                                  <img
                                    className="checklist__attachments__image"
                                    src={attachment}
                                    alt={_i.toString()}
                                    width={100}
                                    height={100}
                                  />
                                </PhotoView>
                              </div>
                            );
                          })}
                        </PhotoProvider>
                      </div>
                    </div>
                  )}
                  {selectedCheckItem?.CHECK_TITLE && (
                    <div className="checklist__memo">
                      <div className="checklist__memo__head">
                        <MessageIcon />
                        <span className="checklist__memo__head-text">메모</span>
                      </div>
                      <div className="checklist__memo__body">{selectedCheckItem?.CHECK_TITLE}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </PlainNavbar>
      </div>
      {employeeModal && <EmployeeModal employeeId={selectedEmployees} onClose={toggleEmployeeModal} />}
    </>
  );
};
export default ChecklistItem;
